import { useId, useState } from 'react'
import type { AccountSummary } from '@/api/accounts'
import { AmountCalculatorInput } from '@/components/AmountCalculatorInput'
import { DatePicker } from '@/components/DatePicker'
import { Select } from '@/components/ui/select'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { today } from '@/lib/transactionDateRangeFilter'
import { cn } from '@/lib/utils'
import { kindInfo } from './addKinds'
import { AmountHero, ChoiceChips, Field, FieldError, FormShell, InfoNote } from './fields'
import { cardOutstanding, errorText, transactableAccounts, useCreateTransaction } from './formLogic'

type AmountChoice = 'full' | 'half' | 'other'

function ordinal(day: number): string {
  if (day % 10 === 1 && day % 100 !== 11) return `${day}st`
  if (day % 10 === 2 && day % 100 !== 12) return `${day}nd`
  if (day % 10 === 3 && day % 100 !== 13) return `${day}rd`
  return `${day}th`
}

/** Paying a card is a transfer into it: the purchases on the card were already the spending. */
export function CardBillForm({
  accounts,
  initialCardId,
  initialFromAccountId,
  onBack,
  onClose,
}: {
  accounts: AccountSummary[]
  initialCardId?: string
  initialFromAccountId?: string
  onBack?: () => void
  onClose: () => void
}) {
  const formId = useId()
  const info = kindInfo('cardBill')
  const { format } = useFormatCurrency()
  const cards = accounts.filter((a) => a.type === 'CREDIT_CARD')
  const payFrom = transactableAccounts(accounts).filter((a) => a.type !== 'CREDIT_CARD')

  const [cardId, setCardId] = useState(() =>
    cards.some((c) => c.id === initialCardId)
      ? initialCardId!
      : cards.length === 1
        ? cards[0].id
        : (cards.find((c) => cardOutstanding(c) > 0)?.id ?? '')
  )
  const card = cards.find((c) => c.id === cardId)
  const owed = card ? cardOutstanding(card) : 0
  const [choice, setChoice] = useState<AmountChoice>(owed > 0 ? 'full' : 'other')
  const [otherText, setOtherText] = useState('')
  const [fromId, setFromId] = useState(() =>
    payFrom.some((a) => a.id === initialFromAccountId) ? initialFromAccountId! : ''
  )
  const [date, setDate] = useState(today())
  const [errors, setErrors] = useState<{ card?: string; amount?: string; from?: string }>({})
  const create = useCreateTransaction(onClose)

  const amountPaise =
    choice === 'full'
      ? toPaise(owed)
      : choice === 'half'
        ? Math.round(toPaise(owed) / 2)
        : toPaise(Number(otherText) || 0)
  const amount = fromPaise(amountPaise)
  const overPaise = amountPaise - toPaise(owed)

  function pickCard(id: string) {
    setCardId(id)
    const next = cards.find((c) => c.id === id)
    setChoice(next && cardOutstanding(next) > 0 ? 'full' : 'other')
    setErrors({})
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const next: typeof errors = {}
    if (!card) next.card = 'Choose a card'
    if (amountPaise <= 0) next.amount = 'Amount must be positive'
    if (!fromId) next.from = 'Choose the account you paid from'
    setErrors(next)
    if (Object.keys(next).length > 0 || !card) return
    create.mutate({
      type: 'TRANSFER',
      fromAccountId: fromId,
      toAccountId: card.id,
      amount,
      date,
      description: `Card bill: ${card.name}`,
    })
  }

  return (
    <FormShell
      title={info.label}
      Icon={info.Icon}
      tone={info.tone}
      onBack={onBack}
      onClose={onClose}
      formId={cards.length > 0 ? formId : undefined}
      submitLabel={amountPaise > 0 ? `Pay ${format(amount)}` : 'Pay'}
      submitting={create.isPending}
      error={create.error ? errorText(create.error) : null}
    >
      {cards.length === 0 ? (
        <InfoNote>
          No credit cards yet. Add one in Accounts, then come back here to record paying its bill.
        </InfoNote>
      ) : (
        <form id={formId} onSubmit={submit} className="flex flex-col gap-4">
          <div role="radiogroup" aria-label="Which card" className="flex flex-col gap-1.5">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--fl-text-3)]">
              Which card
            </p>
            {cards.map((c) => {
              const selected = c.id === cardId
              const details = [
                c.creditLimit != null ? `Limit ${format(c.creditLimit)}` : null,
                c.billingDate ? `bill on the ${ordinal(c.billingDate)}` : null,
              ]
                .filter(Boolean)
                .join(' · ')
              return (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => pickCard(c.id)}
                  className={cn(
                    'flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors',
                    selected
                      ? 'border-[var(--fl-primary)] bg-[var(--fl-primary-faint)]'
                      : 'border-[var(--fl-border)] hover:bg-[var(--fl-surface-hover)]'
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border-2',
                      selected ? 'border-[var(--fl-primary)]' : 'border-[var(--fl-border)]'
                    )}
                  >
                    {selected && <span className="h-2 w-2 rounded-full bg-[var(--fl-primary)]" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14.5px] font-medium text-[var(--fl-text-1)]">
                      {c.name}
                    </span>
                    {details && (
                      <span className="block text-xs text-[var(--fl-text-2)]">{details}</span>
                    )}
                  </span>
                  <span className="text-right">
                    <span
                      className={cn(
                        'block font-serif text-base font-semibold tabular-nums',
                        cardOutstanding(c) > 0
                          ? 'text-[var(--fl-expense)]'
                          : 'text-[var(--fl-text-2)]'
                      )}
                    >
                      {format(cardOutstanding(c))}
                    </span>
                    <span className="block text-[11px] text-[var(--fl-text-3)]">owed</span>
                  </span>
                </button>
              )
            })}
            <FieldError message={errors.card} />
          </div>

          {card && owed > 0 && (
            <ChoiceChips<AmountChoice>
              label="How much"
              value={choice}
              onChange={setChoice}
              options={[
                { value: 'full', label: `Full ${format(owed)}` },
                {
                  value: 'half',
                  label: `Half ${format(fromPaise(Math.round(toPaise(owed) / 2)))}`,
                },
                { value: 'other', label: 'Other amount' },
              ]}
            />
          )}
          {card && owed === 0 && <InfoNote>Nothing is owed on this card right now.</InfoNote>}

          {choice === 'other' ? (
            <AmountHero label="Paying" error={errors.amount}>
              <AmountCalculatorInput value={otherText} onChange={setOtherText} placeholder="0.00" />
            </AmountHero>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <span className="text-xs font-medium text-[var(--fl-text-2)]">Paying</span>
              <span
                data-testid="card-bill-amount"
                className="font-serif text-[34px] font-semibold tabular-nums text-[var(--fl-text-1)]"
              >
                {format(amount)}
              </span>
            </div>
          )}

          {overPaise > 0 && card && (
            <InfoNote tone="warn">
              {format(fromPaise(overPaise))} more than you owe. {card.name} will show{' '}
              {format(fromPaise(overPaise))} in your favour — fine for a prepayment.
            </InfoNote>
          )}

          <Field label="Pay from" error={errors.from}>
            <Select aria-label="Pay from" value={fromId} onChange={setFromId}>
              <option value="">Select account</option>
              {payFrom.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date">
            <DatePicker value={date} onChange={setDate} />
          </Field>
          {card && (
            <InfoNote>
              Saved as a transfer into {card.name}. It isn&rsquo;t spending — the card purchases
              already were.
            </InfoNote>
          )}
        </form>
      )}
    </FormShell>
  )
}
